import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Paper,
  Box,
  Tooltip,
  IconButton,
  CircularProgress,
  Button,
  TableContainer,
  Card,
  CardContent,
  CardHeader,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import {
  WorkflowData,
  WorkflowDetailData,
  initialWorkflowDetailData,
} from "../../types/Workflow";
import {
  createWorkflowDetailData,
  updateWorkflowDetailData,
  getWorkflowDetailData,
  deleteWorkflowDetailData,
  moveUp,
  createFirstWorkflowDetailData,
} from "../../api/WorkflowApi";
import { getData as getRoleData } from "../../api/RoleApi";
import { getData as getStatusData } from "../../api/StatusApi";
import { AddCircleSharp, ArrowForwardRounded } from "@mui/icons-material";
import { ListData } from "../../types/List";
import { toast } from "react-toastify";
import { hasModuleActionPermission } from "../../utils/RolePermissions";
import { AppActionEnum, ModuleEnum } from "../../utils/AppEnum";
import WorkflowDetailForm from "./WorkflowDetailForm";
import { getWorkflowDetailTableColumns } from "./table/ColumnHeader";
import { WorkflowTable } from "../../components/common/WorkflowTable";
import { showConfirmation } from "../../utils/SwalHelper";

interface ComponentProps {
  workflow: WorkflowData;
  onBack: () => void;
}

const WorkflowDiagram: React.FC<ComponentProps> = ({ workflow, onBack }) => {
  const [inputData, setInputData] = useState<WorkflowDetailData>();
  const [listData, setListData] = useState<WorkflowDetailData[]>([]);
  const [statusData, setStatusData] = useState<ListData[]>([]);
  const [roleData, setRoleData] = useState<ListData[]>([]);
  const [open, setOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [loading, setLoading] = useState(true); // <-- loading state

  const loadData = async () => {
    setLoading(true);

    if (workflow.id > 0) {
      const result = await getWorkflowDetailData(workflow.id);
      if (result == null) {
        toast.error("Failed to get list of workflows");
        return;
      }
      await setListData(result);
    }

    const roleResp = await getRoleData();
    if (roleResp == null) {
      toast.error("Failed to get list of roles");
      return;
    }
    await setRoleData(roleResp);

    const statusResp = await getStatusData();
    if (statusResp == null) {
      toast.error("Failed to get list of statuses");
      return;
    }
    await setStatusData(statusResp);

    setLoading(false);
  };

  const refreshData = async () => {
    setLoading(true);

    if (workflow.id > 0) {
      const result = await getWorkflowDetailData(workflow.id);
      if (result == null) {
        toast.error("Failed to get list of workflows");
        return;
      }
      await setListData(result);
    }
    setLoading(false);
  };
  const getWorflowDiagram = (dataList: any[], roleData: any[]) => {
    const merged: any[] = [];

    dataList.forEach((item) => {
      // Find stage by taskLevel only
      const existing = merged.find((m) => m.taskLevel === item.taskLevel);

      if (existing) {
        // Merge roles only inside same taskLevel
        if (!existing.roleIds.includes(item.roleId)) {
          existing.roleIds.push(item.roleId);
        }
      } else {
        merged.push({
          ...item,
          roleIds: [item.roleId],
        });
      }
    });

    // Attach role names
    merged.forEach((m) => {
      const roleNames = (m.roleIds || [])
        .map((id: number) => {
          const role = roleData.find((r: any) => r.id === id);
          return role ? role.name : "N/A";
        })
        .filter(Boolean);

      m.roleNames = roleNames.length > 0 ? roleNames : ["N/A"];
    });

    // Sort by taskLevel ascending
    merged.sort((a, b) => a.taskLevel - b.taskLevel);

    return merged;
  };

  const handleAdd = async (
    data: WorkflowDetailData,
    direction: number,
    isFirst: boolean
  ) => {
    setIsEdit(false);
    data.id = 0;
    data.direction = direction;
    data.workflowId = workflow.id;
    data.taskLevel = data.taskLevel || 1;
    data.location = "";
    data.roleId = 0;
    data.statusId = 0;
    data.canSend = false;
    data.canSkip = false;
    data.isFirst = isFirst;

    setInputData(data);
    setOpen(true);
  };

  const handleEdit = async (data: WorkflowDetailData) => {
    await setInputData(data);
    await setIsEdit(true);
    setOpen(true);
  };

  const handleDelete = async (id: number) => {
    try {
      const confirmed = await showConfirmation(
        "Are you sure?",
        "Do you want to delete this record permanently?",
        "warning",
        "Yes, delete it!"
      );

      if (confirmed) {
        const resp = await deleteWorkflowDetailData(id);
        if (!resp) {
          toast.error("Failed to delete record");
          return;
        }
        refreshData();
        toast.success("Record successfully deleted.");
      }
    } catch (error: any) {
      console.error("Delete failed:", error);
      toast.error(error.response?.data?.message || "Error deleting record");
    }
  };

  const handleMoveUp = async (data: Partial<WorkflowDetailData>) => {
    const resp = await moveUp(data);
    if (!resp) {
      toast.error("Failed to move record up.");
      return;
    }
    refreshData();
  };

  const onSubmit = async (data: Partial<WorkflowDetailData>) => {
    setIsEdit(false);

    if (data.isFirst) {
      const resp = await createFirstWorkflowDetailData(data);

      if (resp == null) {
        toast.error("Failed to create record");
        return;
      }
      toast.success("Record successfully created.");
      setOpen(false);
      loadData();
      return;
    }

    if (data?.id) {
      const resp = await updateWorkflowDetailData(data.id, data);
      if (resp == null) {
        toast.error("Update failed.");
        return;
      }

      toast.success("Record successfully updated.");
    } else {
      const resp = await createWorkflowDetailData(data);

      if (resp == null) {
        toast.error("Failed to create record");
        return;
      }
      toast.success("Record successfully created.");
    }
    setOpen(false);
    loadData();
  };

  useEffect(() => {
    loadData();
  }, [workflow]);

  return (
    <Card
      elevation={3}
      sx={{
        p: 2,
        borderRadius: 3,
        backgroundColor: "#ffffff",
      }}
    >
      {/* Page Title */}
      <CardHeader
        title={`Workflow Detail Of ${workflow.name}`}
        titleTypographyProps={{
          fontSize: 20,
          fontWeight: "bold",
        }}
        sx={{ pb: 1 }}
        action={
          <Button
            variant="outlined"
            color="primary"
            onClick={onBack}
            sx={{ textTransform: "none" }}
          >
            Back
          </Button>
        }
      />

      <CardContent sx={{ pt: 0 }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: 3,
              p: 3,
            }}
          >
            {getWorflowDiagram(listData, roleData).length > 0 ? (
              getWorflowDiagram(listData, roleData).map((data, index) => (
                <Paper
                  key={data.id}
                  elevation={4}
                  sx={{
                    p: 2,
                    width: 280,
                    position: "relative",
                    borderRadius: 3,
                    backgroundColor: "#f9fafb",
                    border: "1px solid #e0e0e0",
                    textAlign: "left",
                    transition: "0.3s",
                    "&:hover": {
                      transform: "scale(1.03)",
                      boxShadow: "0 6px 12px rgba(0,0,0,0.1)",
                    },
                  }}
                >
                  {/* Stage number */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: -12,
                      left: -12,
                      backgroundColor: "#142c83ff",
                      color: "#fff",
                      borderRadius: "50%",
                      width: 28,
                      height: 28,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 14,
                      fontWeight: "bold",
                    }}
                  >
                    {index + 1}
                  </Box>

                  <Box sx={{ mb: 1 }}>
                    <strong>
                      {data.statuses.description} ({data.roleNames.join(", ")})
                    </strong>
                  </Box>

                  {hasModuleActionPermission(
                    ModuleEnum.Workflow,
                    AppActionEnum.Create
                  ) && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mt: 1,
                      }}
                    >
                      {data.statuses.id > 1 && (
                        <Tooltip title="Add stage before">
                          <IconButton
                            color="primary"
                            onClick={() => handleAdd(data, 1, false)}
                            size="small"
                          >
                            <ArrowBackIcon />
                          </IconButton>
                        </Tooltip>
                      )}

                      {data.statuses.name !== "Completed" && (
                        <>
                          {data.statuses.id > 1 && (
                            <Tooltip title="Add stage at same position">
                              <IconButton
                                color="primary"
                                onClick={() => handleAdd(data, 2, false)}
                                size="small"
                              >
                                <AddIcon />
                              </IconButton>
                            </Tooltip>
                          )}

                          <Tooltip title="Add stage after">
                            <IconButton
                              color="primary"
                              onClick={() => handleAdd(data, 3, false)}
                              size="small"
                            >
                              <ArrowForwardRounded />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  )}
                </Paper>
              ))
            ) : (
              <Box sx={{ textAlign: "center", color: "#777", py: 4 }}>
                <Box sx={{ flex: { xs: "0 0 100%", md: "0 0 8%" } }}>
                  <Button
                    fullWidth
                    startIcon={<AddCircleSharp />}
                    onClick={() =>
                      handleAdd(initialWorkflowDetailData, 2, true)
                    }
                    variant="contained"
                    color="primary"
                  >
                    Start Workflow Stages
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        )}

        <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
          <WorkflowTable
            data={listData}
            columns={getWorkflowDetailTableColumns(roleData)}
            actions={{
              moveup: (row) => handleMoveUp(row),
              edit: (row) => handleEdit(row),
              delete: (row) => handleDelete(row.id),
            }}
          />
        </TableContainer>

        <Dialog
          open={open}
          onClose={(event, reason) => {
            if (reason === "backdropClick" || reason === "escapeKeyDown")
              return;
            setInputData(initialWorkflowDetailData);
            setOpen(false);
          }}
          disableEscapeKeyDown
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {isEdit ? "Edit Workflow Detail" : "Add Workflow Detail"}
          </DialogTitle>

          <DialogContent>
            <WorkflowDetailForm
              onSubmit={onSubmit}
              initialData={inputData || initialWorkflowDetailData}
              onClose={() => {
                setInputData(initialWorkflowDetailData);
                setOpen(false);
              }}
              roleData={roleData}
              statusData={statusData}
            />
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default WorkflowDiagram;
